import { cx } from '@orn/shared-web/utils/cx';
import Markdown from 'react-markdown';

import type { IChatMessage } from '../../hooks/useChatWebSocket';

interface IChatMessageProps {
  message: IChatMessage;
}

/**
 * Renders a single chat message with appropriate styling for user vs assistant.
 */
const ChatMessage = ({ message }: IChatMessageProps) => {
  const isUser = message.role === 'user';

  return (
    <div className={cx('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cx(
          'max-w-[85%] rounded-2xl px-4 py-3',
          isUser ? 'bg-brand-solid text-white' : 'bg-secondary text-primary'
        )}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.content}</div>
        ) : (
          <div className="prose prose-sm text-primary max-w-none">
            <Markdown
              components={{
                // Custom styling for markdown elements
                h2: ({ children }) => (
                  <div className="text-primary mb-2 mt-3 text-base font-semibold first:mt-0">{children}</div>
                ),
                h3: ({ children }) => (
                  <div className="text-primary mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</div>
                ),
                p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
                strong: ({ children }) => <span className="font-semibold">{children}</span>,
                ul: ({ children }) => <ul className="mb-2 list-inside list-disc">{children}</ul>,
                ol: ({ children }) => <ol className="mb-2 list-inside list-decimal">{children}</ol>,
                li: ({ children }) => <li className="mb-0.5">{children}</li>,
              }}
            >
              {message.content}
            </Markdown>
          </div>
        )}
        <div className={cx('mt-1 text-xs', isUser ? 'text-white/70' : 'text-tertiary')}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
};

/**
 * Format time as HH:MM AM/PM
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export default ChatMessage;
